import type { FC } from 'react';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { Container, OverflowLabel } from './multi-location-display.styles';
import { useVisibleChildren } from './use-visible-children';

type MultiLocationDisplayProps = {
  locations: Array<string | null | undefined>;
};

export const MultiLocationDisplay: FC<MultiLocationDisplayProps> = ({
  locations,
}) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const overflowLabelRef = useRef<HTMLSpanElement>(null);

  // Filter out null/undefined values from the locations array
  const filteredLocations = locations.filter(Boolean);

  const { visibleCount, hiddenCount, hasMeasured } = useVisibleChildren(
    containerRef,
    wrapperRef,
    overflowLabelRef,
    filteredLocations.length
  );

  const visibleLocations = filteredLocations.slice(0, visibleCount);

  return (
    <Container ref={containerRef}>
      <span ref={wrapperRef}>
        {visibleLocations.map((loc, i) => (
          <span key={loc}>
            {loc}
            {i < visibleLocations.length - 1 ? ', ' : ''}
          </span>
        ))}
      </span>
      <OverflowLabel
        ref={overflowLabelRef}
        className={hasMeasured && hiddenCount > 0 ? 'visible' : ''}
        {...(hiddenCount > 0 && { 'aria-hidden': 'true' })}
      >
        {intl.formatMessage(
          { id: 'EP_JOBS_LOCATIONS_MORE' },
          { remaining: hiddenCount || 1 }
        )}
      </OverflowLabel>
    </Container>
  );
};
